import Phaser from 'phaser';
import { getDefaultStore } from 'jotai/vanilla';
import {
  startTimestampAtom,
  elapsedSecondsAtom,
  isTimerRunningAtom,
  pausedDurationAtom,
  pauseStartTimestampAtom,
  isPausedAtom,
} from '../atoms/timetracker.atom.js';
import type { GlobalSignals } from '@repo/plugin-signals';
import type { SignalSubscription } from '@repo/plugin-signals';

/**
 * TimeTrackerPlugin - Phaser Scene Plugin for time tracking
 *
 * Automatically tracks elapsed time during gameplay:
 * - Starts timer when gameStart signal is received
 * - Updates elapsedSeconds every second while game is running
 * - Stops timer when gameEnd signal is received
 * - Pauses/resumes based on gamePause/gameResume signals
 * - Resets on game restart
 *
 * Note: Focus detection (pause on lost focus) is handled by GameWrapper
 * when the pauseOnLostFocus feature is enabled.
 *
 * Usage in scenes:
 *   this.timetracker.getElapsedSeconds()
 *   this.timetracker.isRunning()
 */
export class TimeTrackerPlugin extends Phaser.Plugins.ScenePlugin {
  private store = getDefaultStore();
  private updateTimer?: Phaser.Time.TimerEvent;
  private subscriptions: SignalSubscription[] = [];
  private signals?: GlobalSignals;

  constructor(scene: Phaser.Scene, pluginManager: Phaser.Plugins.PluginManager) {
    super(scene, pluginManager, 'timetracker');
  }

  /**
   * Initialize plugin and set up signal listeners
   */
  boot(): void {
    // Try to get signals immediately, or wait for them to be available
    this.tryInitializeSignals();

    // Clean up on scene shutdown
    if (this.scene) {
      this.scene.events.once('shutdown', this.shutdown, this);
    }
  }

  /**
   * Try to initialize signals - if not available, wait for registry change
   */
  private tryInitializeSignals(): void {
    // Get signals from the game registry where SignalsPlugin stores them
    this.signals = this.game.registry.get('__signals_global__') as GlobalSignals | undefined;

    if (this.signals) {
      this.setupSignalSubscriptions();
      return;
    }

    // Signals not available yet - listen for registry changes
    // This happens when TimeTrackerPlugin boots before SignalsPlugin
    const onRegistryChange = (
      _parent: Phaser.Data.DataManager,
      key: string,
      data: unknown
    ) => {
      if (key === '__signals_global__' && data) {
        this.signals = data as GlobalSignals;
        this.setupSignalSubscriptions();
        // Remove listener once we have signals
        this.game.registry.events.off('setdata', onRegistryChange);
      }
    };

    this.game.registry.events.on('setdata', onRegistryChange);
  }

  /**
   * Set up signal subscriptions once signals are available
   */
  private setupSignalSubscriptions(): void {
    if (!this.signals || this.subscriptions.length > 0) {
      return; // Already subscribed or no signals
    }

    // Listen to lifecycle signals
    this.subscriptions.push(
      this.signals.lifecycle.gameStart.subscribe(() => this.handleGameStart())
    );

    this.subscriptions.push(
      this.signals.lifecycle.gameEnd.subscribe(() => this.handleGameEnd())
    );

    this.subscriptions.push(
      this.signals.lifecycle.gamePause.subscribe(() => this.handleGamePause())
    );

    this.subscriptions.push(
      this.signals.lifecycle.gameResume.subscribe(() => this.handleGameResume())
    );
  }

  /**
   * Handle game start - set timestamp and start updating
   */
  private handleGameStart(): void {
    const now = Date.now();
    this.store.set(startTimestampAtom, now);
    this.store.set(elapsedSecondsAtom, 0);
    this.store.set(pausedDurationAtom, 0);
    this.store.set(pauseStartTimestampAtom, undefined);
    this.store.set(isPausedAtom, false);
    this.startUpdating();
  }

  /**
   * Handle game end - stop timer and reset
   */
  private handleGameEnd(): void {
    this.stopUpdating();
    this.reset();
  }

  /**
   * Handle game pause - stop updating and record pause start time
   */
  private handleGamePause(): void {
    const now = Date.now();
    this.store.set(pauseStartTimestampAtom, now);
    this.store.set(isPausedAtom, true);
    this.stopUpdating();
  }

  /**
   * Handle game resume - accumulate paused duration and resume updating
   */
  private handleGameResume(): void {
    const startTimestamp = this.store.get(startTimestampAtom);
    const pauseStartTimestamp = this.store.get(pauseStartTimestampAtom);

    if (startTimestamp !== undefined && pauseStartTimestamp !== undefined) {
      // Calculate how long we were paused and add to accumulated paused duration
      const now = Date.now();
      const pauseDuration = now - pauseStartTimestamp;
      const currentPausedDuration = this.store.get(pausedDurationAtom);
      this.store.set(pausedDurationAtom, currentPausedDuration + pauseDuration);
    }

    this.store.set(pauseStartTimestampAtom, undefined);
    this.store.set(isPausedAtom, false);

    if (startTimestamp !== undefined) {
      this.startUpdating();
    }
  }

  /**
   * Start the update timer
   */
  private startUpdating(): void {
    // Clear any existing timer
    this.stopUpdating();

    // Update immediately
    this.updateElapsedTime();

    // Create a timer that updates every second
    if (this.scene) {
      this.updateTimer = this.scene.time.addEvent({
        delay: 1000, // 1 second
        callback: this.updateElapsedTime,
        callbackScope: this,
        loop: true,
      });
    }
  }

  /**
   * Stop the update timer
   */
  private stopUpdating(): void {
    if (this.updateTimer) {
      this.updateTimer.destroy();
      this.updateTimer = undefined;
    }
  }

  /**
   * Update elapsed seconds based on current timestamp, accounting for paused time
   */
  private updateElapsedTime(): void {
    const startTimestamp = this.store.get(startTimestampAtom);

    if (startTimestamp === undefined) {
      return;
    }

    const now = Date.now();
    const pausedDuration = this.store.get(pausedDurationAtom);

    // Subtract accumulated paused duration from total elapsed time
    const elapsedMs = now - startTimestamp - pausedDuration;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);

    this.store.set(elapsedSecondsAtom, elapsedSeconds);
  }

  /**
   * Reset the timer
   */
  private reset(): void {
    this.store.set(startTimestampAtom, undefined);
    this.store.set(elapsedSecondsAtom, 0);
    this.store.set(pausedDurationAtom, 0);
    this.store.set(pauseStartTimestampAtom, undefined);
    this.store.set(isPausedAtom, false);
  }

  // ==================== Public API ====================

  /**
   * Get elapsed seconds
   */
  getElapsedSeconds(): number {
    return this.store.get(elapsedSecondsAtom);
  }

  /**
   * Get start timestamp
   */
  getStartTimestamp(): number | undefined {
    return this.store.get(startTimestampAtom);
  }

  /**
   * Check if timer is running (not paused and started)
   */
  isRunning(): boolean {
    return this.store.get(isTimerRunningAtom);
  }

  /**
   * Check if game is currently paused
   */
  isPaused(): boolean {
    return this.store.get(isPausedAtom);
  }

  /**
   * Manually pause the game (emits gamePause signal)
   */
  pause(): void {
    const startTimestamp = this.store.get(startTimestampAtom);
    const isPaused = this.store.get(isPausedAtom);

    if (startTimestamp !== undefined && !isPaused) {
      this.signals?.lifecycle.gamePause.emit({
        timestamp: Date.now(),
        sceneKey: this.scene?.scene.key ?? 'unknown',
      });
    }
  }

  /**
   * Manually resume the game (emits gameResume signal)
   */
  resume(): void {
    const startTimestamp = this.store.get(startTimestampAtom);
    const isPaused = this.store.get(isPausedAtom);

    if (startTimestamp !== undefined && isPaused) {
      this.signals?.lifecycle.gameResume.emit({
        timestamp: Date.now(),
        sceneKey: this.scene?.scene.key ?? 'unknown',
      });
    }
  }

  /**
   * Format elapsed time as MM:SS
   */
  getFormattedTime(): string {
    const seconds = this.getElapsedSeconds();
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Manually reset the timer (for debugging or special cases)
   */
  manualReset(): void {
    this.stopUpdating();
    this.reset();
  }

  /**
   * Cleanup when plugin is destroyed
   */
  shutdown(): void {
    this.stopUpdating();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * Cleanup when plugin is destroyed completely
   */
  override destroy(): void {
    this.shutdown();
    super.destroy();
  }
}
